import {
  formatCreditCardInfo,
  formatPaginatedOrders,
  formatRedeemAccessCode,
  formatRequestOptions,
} from "./format";
import useRequest from "~/hooks/use-request";
import useUserStore from "~/stores/user";
import useSegment from "~/hooks/use-segment";
import SubscriptionAPI from "~/services/subscription";

const updateStoredCreditCard = (card: IModelCreditCard | null) => {
  const userStore = useUserStore();
  userStore.creditCard = card;
};
const BillingAPI = {
  load(): Promise<IModelCreditCard | null> {
    const userStore = useUserStore();

    if (userStore.creditCard)
      return Promise.resolve(userStore.creditCard);

    const { request } = useRequest();
    return request
      .get<IContentResponse<IResponseBillingInfo>>("/api/commerce/get_billing_info")
      .then((response) => {
        if (Object.keys(response.content).length !== 0) {
          userStore.creditCard = formatCreditCardInfo(response.content.card);
          return formatCreditCardInfo(response.content.card);
        }
        return null;
      });
  },

  loadHistory(generalOptions?: IRequestPaginatedOptions, options?: { loadCommerce?: boolean }): Promise<IPaginatedResponse<IModelBillingOrder>> {
    const { request } = useRequest();
    const userStore = useUserStore();
    const params = formatRequestOptions(generalOptions);

    return Promise.all([
      request.get<IPaginatedResponse<IResponsePPVTicket>>(
        "/api/commerce/get_customer_orders",
        { query: params },
      ),
      request.get<IPaginatedResponse<IResponseCustomerSubscription>>(
        "/api/commerce/customer_contracts",
        { query: params },
      ),
    ]).then((response) => {
      const orders = formatPaginatedOrders(response, generalOptions?.page, generalOptions?.pageSize);

      if (options?.loadCommerce) {
        if (response[0].content.entryCount)
          userStore.hasPPVTickets = true;

        if (response[1].content.entryCount)
          userStore.hasSubscriptions = true;
      }

      return orders;
    });
  },

  update(tokenID: string): Promise<void> {
    const { request } = useRequest();
    const payload = {
      card: { token: tokenID },
    };

    return request
      .post<IContentResponse<IResponseBillingInfo>>("/api/commerce/set_billing_info", { body: payload })
      .then(response => updateStoredCreditCard(formatCreditCardInfo(response.content.card)));
  },

  delete(): Promise<void> {
    const { request } = useRequest();
    const userStore = useUserStore();
    const payload = { card_id: userStore.creditCard?.id, user_id: userStore.user?.id };

    return request
      .post("/api/commerce/delete_credit_card", { body: payload })
      .then(() => updateStoredCreditCard(null));
  },

  redeemAccessCode(code: string): Promise<IModelCommerce> {
    const { request } = useRequest();
    const segment = useSegment();

    return request
      .post<IContentResponse<IResponseReedemCode>>("/api/commerce/redeem_code", { body: { code } })
      .then((response) => {
        segment.track("User Used Access Code", { code });

        return formatRedeemAccessCode(response.content);
      });
  },

  async createStripeToken(card: IModelStripeCC): Promise<string> {
    const stripe = useClientStripe();
    const cardHolderName = card.cardHolderName;
    const cardElement = card.cardNumber.el;
    const { token, error } = await stripe.createToken(cardElement, { name: cardHolderName });

    if (error) {
      console.error(error.message);
      throw new Error("ERR400GN00"); // Error ("Bad Request")
    }

    return token.id;
  },

  async payWithStripeCreditCard(subscriptionId: string, card: IModelStripeCC): Promise<void> {
    try {
      const tokenID = await BillingAPI.createStripeToken(card);
      await SubscriptionAPI.subscribeWithCreditCard(subscriptionId, tokenID);
    } catch (error: any) {
      throw new Error(error.message);
    }
  },

  async updateStripeCreditCard(card: IModelStripeCC): Promise<void> {
    try {
      const tokenID = await BillingAPI.createStripeToken(card);
      await BillingAPI.update(tokenID);
    } catch (error: any) {
      throw new Error(error.message);
    }
  },
};

export default BillingAPI;
